import { NotFoundCoreError } from '@bitsentry-ce/core/kernel';
import { getErrorMessage } from '../../../../shared/errors';
import type { TicketsUseCases } from '../ports/inbound';
import type {
  TicketProviderRegistryPort,
  TicketsRepositoryPort,
} from '../ports/outbound';
import { TicketStatus } from '../../domain';
import type {
  CreatedTicket,
  CreatedTicketsQuery,
  CreatedTicketsResult,
  CreatedTicketsWhere,
  ProviderConnectionResult,
  TicketStatistics,
  TicketStatusResult,
} from '../ports/types';

const OPEN_STATUSES = new Set<TicketStatus>([
  TicketStatus.NEW,
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.PENDING,
]);
const RESOLVED_STATUSES = new Set<TicketStatus>([
  TicketStatus.RESOLVED,
  TicketStatus.CLOSED,
]);

export class TicketsUseCasesImpl implements TicketsUseCases {
  constructor(
    private readonly ticketsRepository: TicketsRepositoryPort,
    private readonly providerRegistry: TicketProviderRegistryPort,
  ) {}

  async findCreatedTickets(
    query: CreatedTicketsQuery,
  ): Promise<CreatedTicketsResult> {
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    const sortBy = query.sortBy ?? 'createdAt';
    const sortOrder = query.sortOrder ?? 'desc';
    const where = this.buildCreatedTicketsWhere(query);

    const [tickets, total] = await Promise.all([
      this.ticketsRepository.findCreatedTickets({
        where,
        orderBy: { [sortBy]: sortOrder },
        skip: (page - 1) * limit,
        take: limit,
      }),
      this.ticketsRepository.countCreatedTickets(where),
    ]);

    return {
      data: tickets,
      meta: {
        total,
        page,
        limit,
        totalPages: Math.ceil(total / limit),
      },
    };
  }

  async findOne(id: string): Promise<CreatedTicket> {
    const ticket = await this.ticketsRepository.findOne(id);
    if (ticket == null) {
      throw new NotFoundCoreError('Ticket', id);
    }

    return ticket;
  }

  async getTicketStatus(id: string): Promise<TicketStatusResult> {
    const ticket = await this.ticketsRepository.findStatusView(id);
    if (ticket == null) {
      throw new NotFoundCoreError('Ticket', id);
    }

    const providerId = this.providerRegistry.resolveProviderId(
      ticket.ticketProvider,
    );

    try {
      const provider = this.providerRegistry.get(providerId);
      const statusInfo = await provider.readStatus(ticket.externalTicketId);
      if (statusInfo == null) {
        return ticket;
      }

      const latestStatus = statusInfo.status;
      if (
        statusInfo.statusKnown &&
        latestStatus !== null &&
        latestStatus !== ticket.status
      ) {
        await this.ticketsRepository.updateStatus(id, latestStatus, new Date());
        ticket.status = latestStatus;
      }

      return {
        ...ticket,
        externalStatus: statusInfo.rawStatus,
        provider: providerId,
      };
    } catch {
      return ticket;
    }
  }

  async testProviderConnection(): Promise<ProviderConnectionResult> {
    try {
      const result = await this.providerRegistry.testConnection();
      let message = `${result.provider} connection failed`;
      if (result.success) {
        message = `${result.provider} connection successful`;
      }

      return {
        connected: result.success,
        provider: result.provider,
        message,
        error: result.error,
        timestamp: new Date(),
      };
    } catch (error) {
      const providerId = this.providerRegistry.getDefaultProviderId();
      return {
        connected: false,
        provider: providerId,
        message: `${providerId} connection failed`,
        error: getErrorMessage(error),
        timestamp: new Date(),
      };
    }
  }

  getTicketsByVulnerability(vulnerabilityId: string): Promise<CreatedTicket[]> {
    return this.ticketsRepository.findByVulnerability(vulnerabilityId);
  }

  getTicketsByIncident(incidentId: string): Promise<CreatedTicket[]> {
    return this.ticketsRepository.findByIncident(incidentId);
  }

  async getTicketStatistics(): Promise<TicketStatistics> {
    const [byStatus, byPriority, bySource, recentTickets] = await Promise.all([
      this.ticketsRepository.groupByStatus(),
      this.ticketsRepository.groupByPriority(),
      this.ticketsRepository.groupBySource(),
      this.ticketsRepository.getRecent(5),
    ]);

    const totalTickets = byStatus.reduce((sum, item) => sum + item._count, 0);
    const openTickets = this.sumCounts(
      byStatus.filter((item) => OPEN_STATUSES.has(item.status)),
    );
    const resolvedTickets = this.sumCounts(
      byStatus.filter((item) => RESOLVED_STATUSES.has(item.status)),
    );

    return {
      summary: {
        total: totalTickets,
        open: openTickets,
        resolved: resolvedTickets,
        resolutionRate: this.calculateResolutionRate(
          totalTickets,
          resolvedTickets,
        ),
      },
      byPriority: byPriority.map((item) => ({
        priority: item.priority,
        count: item._count,
      })),
      byStatus: byStatus.map((item) => ({
        status: item.status,
        count: item._count,
      })),
      bySource: bySource.map((item) => ({
        automatic: item.automatic,
        count: item._count,
      })),
      recent: recentTickets,
    };
  }

  private buildCreatedTicketsWhere(
    query: CreatedTicketsQuery,
  ): CreatedTicketsWhere {
    const where: CreatedTicketsWhere = {};

    if (query.status != null) {
      where.status = query.status;
    }

    if (query.priority != null) {
      where.priority = query.priority;
    }

    if (query.sourceType === 'vulnerability') {
      where.vulnerabilityId = { not: null };
    } else if (query.sourceType === 'incident') {
      where.incidentId = { not: null };
    }

    return where;
  }

  private sumCounts(items: Array<{ _count: number }>): number {
    return items.reduce((sum, item) => sum + item._count, 0);
  }

  private calculateResolutionRate(total: number, resolved: number): number {
    if (total === 0) {
      return 0;
    }

    return (resolved / total) * 100;
  }
}
